import React from "react";
import { Info } from "lucide-react";
import { APP } from "@/constants/testIds";
import { COUNTRIES, CURRENCIES } from "@/constants/locale";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const MARKET_TOOLTIP = (
  <div className="max-w-[240px] space-y-2 text-left">
    <p className="font-semibold text-[11px] uppercase tracking-wider opacity-80">
      Market scope
    </p>
    <div className="space-y-1.5 text-xs leading-relaxed">
      <p>
        <span className="font-semibold">Local</span> — single city or
        neighbourhood. Small TAM, fast to launch (e.g. tiffin service in
        Whitefield).
      </p>
      <p>
        <span className="font-semibold">National</span> — country-wide,
        scoped to your selected country (e.g. a D2C brand across India).
      </p>
      <p>
        <span className="font-semibold">Global</span> — cross-border,
        internet-native ideas (SaaS, marketplaces).
      </p>
    </div>
  </div>
);

const CATEGORIES = [
  "Any", "AI & Tech", "Sustainability", "Health & Wellness", "Pet Economy",
  "Silver Tech", "Experience & Education", "Local Services", "Creator Economy",
  "FinTech", "Climate Tech",
];
const BUDGETS_BY_CURRENCY = {
  INR: ["Any", "<₹1L", "₹1L-₹10L", "₹10L-₹1Cr", "₹1Cr+"],
  USD: ["Any", "<$1k", "$1k-$10k", "$10k-$100k", "$100k+"],
  EUR: ["Any", "<€1k", "€1k-€10k", "€10k-€100k", "€100k+"],
  GBP: ["Any", "<£1k", "£1k-£10k", "£10k-£100k", "£100k+"],
  AUD: ["Any", "<A$1k", "A$1k-A$10k", "A$10k-A$100k", "A$100k+"],
  CAD: ["Any", "<C$1k", "C$1k-C$10k", "C$10k-C$100k", "C$100k+"],
  SGD: ["Any", "<S$1k", "S$1k-S$10k", "S$10k-S$100k", "S$100k+"],
  AED: ["Any", "<د.إ4k", "د.إ4k-د.إ40k", "د.إ40k-د.إ400k", "د.إ400k+"],
};
const SKILLS = ["Any", "Beginner", "Intermediate", "Expert"];
const MARKETS = ["Any", "Local", "National", "Global"];
const COUNTS = ["3", "6", "9"];

function Select({ label, value, onChange, options, testId, renderOption, tooltip }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono-tight uppercase tracking-widest text-[color:var(--muted)] flex items-center gap-1">
        {label}
        {tooltip && (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger
                asChild
                onClick={(e) => e.preventDefault()}
              >
                <button
                  type="button"
                  data-testid={`${testId}-info`}
                  aria-label={`About ${label} filter`}
                  className="inline-flex items-center justify-center rounded-full text-[color:var(--muted)] hover:text-[color:var(--accent)] focus:outline-none focus:text-[color:var(--accent)] transition-colors"
                >
                  <Info className="w-3 h-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                align="start"
                className="bg-[color:var(--bg)] text-[color:var(--fg)] border border-[color:var(--fg)] rounded-none px-3 py-2"
              >
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </span>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2.5 bg-transparent border border-[color:var(--fg)] focus:outline-none focus:border-[color:var(--accent)] text-sm font-semibold"
      >
        {options.map((o) => {
          const opt = renderOption ? renderOption(o) : { value: o, label: o };
          return (
            <option key={opt.value} value={opt.value} className="bg-[color:var(--bg)] text-[color:var(--fg)]">
              {opt.label}
            </option>
          );
        })}
      </select>
    </label>
  );
}

export default function IdeaFilters({ filters, setFilters }) {
  const budgets = BUDGETS_BY_CURRENCY[filters.currency] || BUDGETS_BY_CURRENCY.INR;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 sm:gap-4">
      <Select label="Category" value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })} options={CATEGORIES} testId={APP.filterCategory} />
      <Select label="Budget" value={budgets.includes(filters.budget) ? filters.budget : "Any"} onChange={(v) => setFilters({ ...filters, budget: v })} options={budgets} testId={APP.filterBudget} />
      <Select label="Skill" value={filters.skill} onChange={(v) => setFilters({ ...filters, skill: v })} options={SKILLS} testId={APP.filterSkill} />
      <Select label="Market" value={filters.market} onChange={(v) => setFilters({ ...filters, market: v })} options={MARKETS} testId={APP.filterMarket} tooltip={MARKET_TOOLTIP} />
      <Select
        label="Country"
        value={filters.country}
        onChange={(v) => setFilters({ ...filters, country: v })}
        options={COUNTRIES}
        testId={APP.filterCountry}
      />
      <Select
        label="Currency"
        value={filters.currency}
        onChange={(v) => setFilters({ ...filters, currency: v })}
        options={CURRENCIES}
        testId={APP.filterCurrency}
        renderOption={(c) => ({ value: c.code, label: `${c.symbol} ${c.code}` })}
      />
      <Select
        label="Count"
        value={String(filters.count)}
        onChange={(v) => setFilters({ ...filters, count: parseInt(v, 10) })}
        options={COUNTS}
        testId={APP.filterCount}
      />
    </div>
  );
}
